import {
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Error, Hint, Label } from '@progress/kendo-react-labels';
import {
  Editor,
  EditorChangeEvent,
  EditorPasteEvent,
  EditorProps,
  EditorTools,
} from '@progress/kendo-react-editor';
import { useRef } from 'react';
import { cleanPastedHtml } from './cleanPastedHtml.ts';

const {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Indent,
  Outdent,
  UnorderedList,
  OrderedList,
  FormatBlock,
  Unlink,
  InsertImage,
  Link,
  CleanFormatting,
} = EditorTools;

interface EditorStateWrapperProps<T extends FieldValues>
  extends UseControllerProps<T>, Omit<EditorProps, 'defaultValue' | 'name'> {
  hint?: string;
  label?: string;
  optional?: boolean;
  containerClassName?: string;
}

// ProseMirror (Kendo Editor) wraps list item content in <p> by default.
// Strip those wrappers so the stored HTML contains plain <li>text</li>.
// Returns the input unchanged when there is nothing to strip, because
// re-serializing via innerHTML would otherwise rewrite invalid markup.
const stripParagraphsInListItems = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  const paragraphs = div.querySelectorAll('li > p');
  if (paragraphs.length === 0) return html;
  paragraphs.forEach((p) => {
    p.replaceWith(...Array.from(p.childNodes));
  });
  return div.innerHTML;
};

export const EditorStateWrapper = <T extends FieldValues>({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  disabled,
  hint,
  label,
  optional,
  containerClassName,
}: EditorStateWrapperProps<T>) => {
  const {
    field,
    fieldState: { error, isTouched },
  } = useController({
    name,
    rules,
    shouldUnregister,
    defaultValue,
    control,
    disabled,
  });

  const { onChange, onBlur, value, ...restField } = field;
  // Content may come from the offline app; only normalize it after the user
  // has edited it here, so opening and leaving the editor keeps it unchanged.
  const editedRef = useRef(false);
  const id = field.name;
  const showValidationMessage = Boolean(isTouched && error?.message);
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${id}_hint` : '';
  const errorId = showValidationMessage ? `${id}_error` : '';

  return (
    <FieldWrapper
      className={`flex w-full max-w-3xl flex-col [&_.k-editor]:!rounded-md [&_.k-toolbar]:!rounded-t-md ${containerClassName || ''}`}
    >
      <Label
        editorId={id}
        editorValid={!showValidationMessage}
        editorDisabled={disabled}
        optional={optional}
        className="k-form-label mb-1"
      >
        {label}
      </Label>
      <div className={'k-form-field-wrap'}>
        <Editor
          value={value ?? ''}
          onChange={(event: EditorChangeEvent) => {
            // Store raw HTML during editing to avoid re-parsing on every keystroke.
            editedRef.current = true;
            onChange(event?.html ?? '');
          }}
          onBlur={() => {
            // Normalize once when the user leaves the editor after editing.
            if (editedRef.current) {
              editedRef.current = false;
              onChange(stripParagraphsInListItems(value ?? ''));
            }
            onBlur();
          }}
          onPasteHtml={(event: EditorPasteEvent) =>
            cleanPastedHtml(event.pastedHtml)
          }
          tools={[
            [Bold, Italic, Underline, Strikethrough],
            FormatBlock,
            [AlignLeft, AlignCenter, AlignRight, AlignJustify],
            [Indent, Outdent, OrderedList, UnorderedList],
            [Link, Unlink, InsertImage],
            CleanFormatting,
          ]}
          contentStyle={{
            height: 200,
          }}
          {...restField}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{error?.message}</Error>}
      </div>
    </FieldWrapper>
  );
};
